import React, { useState, useEffect } from 'react';
import { FileSignature, Plus, Search, Eye, Trash2, Send, CheckCircle, XCircle, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import CreateRFQModal from './CreateRFQModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  received: { label: 'Received', color: 'bg-yellow-100 text-yellow-800' },
  compared: { label: 'Compared', color: 'bg-indigo-100 text-indigo-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  converted: { label: 'Converted to PO', color: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600' }
};

const RFQs = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  useEffect(() => { fetchRFQs(); }, [statusFilter]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      const response = await fetch(`${API_URL}/api/procurement/rfqs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      setRfqs(await response.json());
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load RFQs' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this RFQ?')) return;
    try {
      await fetch(`${API_URL}/api/procurement/rfqs/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'RFQ deleted' });
      fetchRFQs();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete' });
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'AED' }).format(amount);
  };

  const getTotalAmount = (rfq) => {
    const items = rfq.response_items || rfq.items || [];
    return items.reduce((sum, item) => sum + (item.total || (item.quantity * (item.unit_price || 0))), 0);
  };

  return (
    <div className="p-6 space-y-6" data-testid="rfqs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Request for Quotations</h1>
          <p className="text-sm text-[#6C757D]">Procurement &gt; RFQs</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="create-rfq-btn">
          <Plus className="w-4 h-4 mr-2" /> New RFQ
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6C757D]" />
          <Input placeholder="Search RFQs..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchRFQs()}
            className="pl-10" data-testid="rfq-search-input" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm" data-testid="rfq-status-filter">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-[#6C757D]">Loading RFQs...</div>
          ) : rfqs.length === 0 ? (
            <div className="text-center py-12">
              <FileSignature className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6C757D]">No RFQs found. Create your first RFQ.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#2B2D42]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">RFQ #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Issuer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Issue Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rfqs.map((rfq, idx) => {
                    const sc = STATUS_CONFIG[rfq.status] || STATUS_CONFIG.draft;
                    return (
                      <tr key={rfq.id} className={`hover:bg-gray-50 cursor-pointer ${idx % 2 ? 'bg-gray-50' : ''}`}
                        onClick={() => navigate(`/admin/procurement/rfqs/${rfq.id}`)}
                        data-testid={`rfq-row-${rfq.id}`}>
                        <td className="px-4 py-3 text-sm font-medium text-[#FF6B35]">{rfq.rfq_number}</td>
                        <td className="px-4 py-3 text-sm text-[#2B2D42]">{rfq.company_snapshot?.trading_name || rfq.company_snapshot?.legal_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-[#2B2D42]">{rfq.supplier_snapshot?.name || rfq.supplier_snapshot?.company_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-[#6C757D]">{formatDate(rfq.issue_date)}</td>
                        <td className="px-4 py-3 text-sm text-[#6C757D]">{formatDate(rfq.due_date)}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{formatCurrency(getTotalAmount(rfq), rfq.currency)}</td>
                        <td className="px-4 py-3"><Badge className={sc.color}>{sc.label}</Badge></td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/procurement/rfqs/${rfq.id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/procurement/rfqs/${rfq.id}/compare`)} data-testid={`rfq-compare-${rfq.id}`} title="Compare proposals">
                              <Scale className="w-4 h-4 text-[#FF6B35]" />
                            </Button>
                            {rfq.status === 'draft' && (
                              <Button variant="ghost" size="sm" onClick={(e) => handleDelete(rfq.id, e)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateRFQModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); fetchRFQs(); }} />
    </div>
  );
};

export default RFQs;
